import React, { useEffect } from 'react';
import { css, CSSObject } from '@emotion/css';

import * as EventsManager from '../../system-utils/events-manager';

import { mqBreakpoints, pxToRem } from '../../system-libraries/styles';

import { VMApplicationContentContainer } from '../../system-libraries/styles/containers/vm-application-content-container';
import { CtaButton } from '../buttons';

type Props = {
  pageTitle?: string | null;
  title: string;
  description?: string;
  ctaText?: string;
  onCtaClick?: () => void;
  dataTestId?: string;
  inlineCtaText?: string;
  onInlineCtaClick?: () => void;
};

export const FullPageErrorContent: React.FC<Props> = ({ pageTitle, title, description, ctaText, onCtaClick, dataTestId, inlineCtaText, onInlineCtaClick }) => {
  useEffect(() => {
    if (pageTitle !== null) {
      EventsManager.pageTitle.update(pageTitle ?? title);
    }
  }, [pageTitle]);

  const inlineCta =
    onInlineCtaClick && inlineCtaText ? (
      <a data-testid='nba-error-cta-inline' onClick={onInlineCtaClick}>
        {inlineCtaText}
      </a>
    ) : null;

  return (
    <>
      <VMApplicationContentContainer>
        <div className={css(containerStyle)} data-testid={dataTestId}>
          <h1 data-testid='nba-error-title' className={css(headingStyle)}>
            {title}
          </h1>
          {description ? (
            <p data-testid='nba-error-description' className={css(descriptionStyle)}>
              {description}
              {inlineCta}
            </p>
          ) : null}
          {onCtaClick && ctaText ? (
            <CtaButton data-testid='nba-error-cta' onClick={onCtaClick}>
              {ctaText}
            </CtaButton>
          ) : null}
        </div>
      </VMApplicationContentContainer>
    </>
  );
};

const containerStyle: CSSObject = {
  padding: pxToRem(32),

  [mqBreakpoints.desktop]: {
    padding: `${pxToRem(80)} ${pxToRem(160)}`,
  },
};

const headingStyle: CSSObject = {
  color: 'var(--color-primary)',
  fontFamily: 'var(--font-heading)',
  fontSize: pxToRem(36),
  lineHeight: pxToRem(44),
  letterSpacing: pxToRem(-0.37),
  paddingBottom: pxToRem(24),

  [mqBreakpoints.tablet]: {
    fontSize: pxToRem(48),
    lineHeight: pxToRem(56),
    letterSpacing: pxToRem(-0.75),
    paddingBottom: pxToRem(32),
  },
};

const descriptionStyle: CSSObject = {
  fontFamily: 'var(--font-heading)',
  fontWeight: 'var(--font-weight-regular)',
  fontSize: pxToRem(18),
  lineHeight: pxToRem(28),
  margin: 0,
  paddingBottom: pxToRem(40),

  [mqBreakpoints.tablet]: {
    fontSize: pxToRem(21),
    lineHeight: pxToRem(28),
    paddingBottom: pxToRem(48),
  },
};
